import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface PlantSavingsAnalysisChartProps {
  data: Array<{
    plant_name: string;
    estimated_savings: number;
    released_savings: number;
    realization_rate: number;
    roi: number;
    cost_benefit_ratio: number;
    workshop_count: number;
  }>;
  loading?: boolean;
}

const PlantSavingsAnalysisChart: React.FC<PlantSavingsAnalysisChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Plant Savings Analysis</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Plant Savings Analysis</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No plant savings data available
        </div>
      </div>
    );
  }

  const getRealizationLevel = (rate: number) => {
    if (rate >= 80) return { level: 'Excellent', color: '#10b981' };
    if (rate >= 60) return { level: 'Good', color: '#3b82f6' };
    if (rate >= 40) return { level: 'Average', color: '#f59e0b' };
    return { level: 'Needs Improvement', color: '#ef4444' };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Plant Savings Analysis</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="plant_name" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              yAxisId="left"
              tick={{ fontSize: 12 }}
              domain={[0, 'dataMax + 1000']}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
            />
            <Tooltip 
              formatter={(value: number, name: string) => {
                if (name === 'estimated_savings') return [formatCurrency(value), 'Estimated Savings'];
                if (name === 'released_savings') return [formatCurrency(value), 'Released Savings'];
                if (name === 'realization_rate') return [`${value.toFixed(1)}%`, 'Realization Rate'];
                if (name === 'roi') return [`${value.toFixed(1)}%`, 'ROI'];
                if (name === 'cost_benefit_ratio') return [`${value.toFixed(2)}`, 'Cost-Benefit Ratio'];
                if (name === 'workshop_count') return [`${value} workshops`, 'Workshop Count'];
                return [value, name];
              }}
              labelFormatter={(label) => `Plant: ${label}`}
            />
            <Legend />
            <Bar 
              yAxisId="left"
              dataKey="estimated_savings" 
              name="Estimated Savings"
              fill="#3b82f6"
              radius={[4, 4, 0, 0]}
            />
            <Bar 
              yAxisId="left"
              dataKey="released_savings" 
              name="Released Savings"
              fill="#10b981"
              radius={[4, 4, 0, 0]}
            />
            <Bar 
              yAxisId="right"
              dataKey="realization_rate" 
              name="Realization Rate (%)"
              fill="#f59e0b"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Financial Performance</h4>
          <div className="space-y-2">
            {[...data]
              .sort((a, b) => b.released_savings - a.released_savings)
              .map((item) => {
                const realization = getRealizationLevel(item.realization_rate);
                return (
                  <div key={item.plant_name} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded mr-2" 
                        style={{ backgroundColor: realization.color }}
                      ></div>
                      <span className="text-sm font-medium">{item.plant_name}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{formatCurrency(item.released_savings)}</span>
                      <span className={`ml-2 text-xs px-2 py-1 rounded`} style={{ 
                        backgroundColor: realization.color + '20', 
                        color: realization.color 
                      }}>
                        {item.realization_rate.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Financial Insights</h4>
          <div className="space-y-3">
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Highest Released</span>
                <span className="font-bold text-green-600">
                  {data.reduce((max, item) => item.released_savings > max.released_savings ? item : max, data[0])?.plant_name}
                </span>
              </div>
              <div className="text-xs text-green-500 mt-1">
                {formatCurrency(Math.max(...data.map(d => d.released_savings)))}
              </div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-600">Best ROI</span>
                <span className="font-bold text-blue-600">
                  {data.reduce((max, item) => item.roi > max.roi ? item : max, data[0])?.plant_name}
                </span>
              </div>
              <div className="text-xs text-blue-500 mt-1">
                {Math.max(...data.map(d => d.roi)).toFixed(1)}% ROI
              </div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-purple-600">Best Realization</span>
                <span className="font-bold text-purple-600">
                  {data.reduce((max, item) => item.realization_rate > max.realization_rate ? item : max, data[0])?.plant_name}
                </span>
              </div>
              <div className="text-xs text-purple-500 mt-1">
                {Math.max(...data.map(d => d.realization_rate)).toFixed(1)}% realization
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlantSavingsAnalysisChart;


